import { useState } from 'react';
import type { Portada } from './api.js';
import { Boton, Firma, Pantalla, Seguro } from './ui.js';

/**
 * La pantalla de inicio. Frames `OPCIÓN 1` y `DEGLOSE DE TIENES DUDAS` del Figma — que son la
 * misma pantalla con el acordeón cerrado y abierto.
 *
 * ── LO QUE ESTA PANTALLA REVELÓ DEL PRODUCTO ───────────────────────────────────
 *
 * Muestra `****4571` y `Empresa 1 SAC` **antes de que la persona toque nada**. Eso significa
 * que el flujo no arranca en blanco: arranca desde una sesión que el cliente ya creó por API,
 * con el documento que espera verificar. No es un detalle visual — define la arquitectura.
 *
 * Y el logo de arriba a la izquierda dice "upside" en el diseño: es el logo de un CLIENTE, no
 * el nuestro. La marca blanca no es un extra que se añade después; estaba en el diseño desde
 * el primer frame. Abajo, en pequeño, "Powered by validate".
 *
 * ── REHECHA SOBRE ui.tsx (#78) ─────────────────────────────────────────────────
 *
 * Esta pantalla nació antes que `ui.tsx` y traía sus propias copias del candado, la flecha,
 * el pie y el fondo. Ahora `Pantalla`, `Seguro`, `Boton` y `Firma` salen de ahí: cuando el
 * botón cambie, cambia en un sitio y no en dos que divergen. El acordeón de dudas se queda
 * aquí porque es de esta pantalla y de ninguna otra.
 */

/** Las tres preguntas del acordeón, tal cual el Figma. */
const DUDAS: readonly { q: string; a: string }[] = [
  {
    q: '¿Cuánto tarda?',
    a: 'Menos de un minuto. Necesitas tu documento a mano y buena luz.',
  },
  {
    q: '¿Es seguro?',
    a:
      'Sí. La conexión va cifrada y tus fotos solo se usan para comprobar que eres tú. ' +
      'No se comparten con nadie más.',
  },
  {
    q: '¿Dónde se usa mi información?',
    a:
      'Solo para esta verificación, y solo la ve quien te la pidió. Las imágenes se borran ' +
      'solas pasado el plazo que marca la ley de tu país.',
  },
];

/** El icono de documento de la tarjeta. */
function Documento(): JSX.Element {
  return (
    <svg width="18" height="14" viewBox="0 0 18 14" fill="none" aria-hidden="true">
      <rect x="0.7" y="0.7" width="16.6" height="12.6" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="5.5" cy="6" r="2" fill="currentColor" />
      <path d="M10 5h5M10 8h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

export function Inicio({
  portada,
  onEmpezar,
}: {
  portada: Portada;
  onEmpezar: () => void;
}): JSX.Element {
  const [abierta, setAbierta] = useState<number | null>(null);

  return (
    <Pantalla>
      <div className="flex flex-1 flex-col px-6 pt-5">
        {/* ── Cabecera: el logo del cliente y el sello ─────────────────────────── */}
        <header className="flex items-center justify-between">
          {portada.marca.logoUrl ? (
            // `alt` con el nombre del cliente: quien usa lector de pantalla también tiene
            // derecho a saber para quién se está verificando.
            <img
              src={portada.marca.logoUrl}
              alt={portada.paraQuien}
              className="h-6 max-w-[140px] object-contain"
            />
          ) : (
            // Sin logo NO se pone el nuestro: quien pide la verificación es el cliente, y poner
            // "Valídate" aquí le diría a la persona que la verificación es para nosotros.
            <span className="text-sm font-semibold" style={{ color: '#101114' }}>
              {portada.paraQuien}
            </span>
          )}

          <Seguro />
        </header>

        {/* ── El cuerpo ────────────────────────────────────────────────────────── */}
        <main className="flex flex-1 flex-col justify-center py-8">
          <div className="mb-8" aria-hidden="true">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-2xl"
              style={{ border: '2px solid rgba(51,50,242,.3)', background: 'rgba(51,50,242,.06)' }}
            >
              <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                <circle cx="13" cy="9" r="4.5" fill="var(--acento, #3332f2)" />
                <path d="M4.5 22c0-4.4 3.8-7 8.5-7s8.5 2.6 8.5 7" fill="var(--acento, #3332f2)" />
              </svg>
            </div>
          </div>

          {/* Inter 600. "Verificación" en acento, "de identidad" en tinta — del Figma. */}
          <h1 className="text-[34px] font-semibold leading-[1.1] tracking-tight">
            <span style={{ color: 'var(--acento, #3332f2)' }}>Verificación</span>
            <br />
            <span style={{ color: '#101114' }}>de identidad</span>
          </h1>

          <p className="mt-3 max-w-[19rem] text-[15px] leading-relaxed" style={{ color: 'rgba(16,17,20,.6)' }}>
            Confirma que eres tú en segundos, de forma segura
          </p>

          {/* ── El documento y para quién ──────────────────────────────────────── */}
          {portada.documentoEnmascarado !== null && (
            <div className="mt-8 flex flex-col items-center">
              <div
                className="flex items-center gap-2.5 rounded-xl px-4 py-2.5"
                style={{ border: '1px solid rgba(51,50,242,.3)', color: 'var(--acento, #3332f2)' }}
              >
                <Documento />
                {/* Los últimos cuatro dígitos, nunca el documento entero: quien abre este
                    enlace puede no ser la persona correcta. */}
                <span className="font-semibold tracking-[0.27em]">{portada.documentoEnmascarado}</span>
              </div>
              <p className="mt-2.5 text-[15px] font-semibold" style={{ color: '#101114' }}>
                {portada.paraQuien}
              </p>
            </div>
          )}
        </main>

        {/* ── El pie ───────────────────────────────────────────────────────────── */}
        <footer className="zona-segura">
          <Boton onClick={onEmpezar} disabled={!portada.puedeEmpezar}>
            Comenzar verificación
          </Boton>

          {/* Si no se puede empezar, el motivo va DEBAJO del botón, no en su lugar: una
              pantalla sin botón deja a la persona sin saber si esperar o irse. */}
          {!portada.puedeEmpezar && (
            <p className="mt-3 text-center text-[13px] leading-relaxed" style={{ color: 'rgba(16,17,20,.55)' }}>
              {portada.motivo}
            </p>
          )}

          <div className="mt-5">
            <button
              type="button"
              onClick={() => setAbierta(abierta === null ? -1 : null)}
              className="mx-auto flex items-center gap-1.5 border-b pb-0.5 text-[14px] font-semibold"
              style={{ color: '#101114', borderColor: '#101114' }}
              aria-expanded={abierta !== null}
            >
              ¿Tienes dudas?
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
                <path
                  d={abierta !== null ? 'M1 7l5-5 5 5' : 'M1 1l5 5 5-5'}
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {abierta !== null && (
              <ul className="mt-4 space-y-2.5">
                {DUDAS.map((d, i) => (
                  <li key={d.q} className="rounded-2xl" style={{ border: '1px solid rgba(16,17,20,.12)' }}>
                    <button
                      type="button"
                      onClick={() => setAbierta(abierta === i ? -1 : i)}
                      className="flex w-full items-center justify-between px-4 py-3 text-left text-[14px] font-medium"
                      style={{ color: '#101114' }}
                      aria-expanded={abierta === i}
                    >
                      {d.q}
                      <span style={{ color: 'rgba(16,17,20,.5)' }}>{abierta === i ? '−' : '+'}</span>
                    </button>
                    {abierta === i && (
                      <p className="px-4 pb-3.5 text-[13px] leading-relaxed" style={{ color: 'rgba(16,17,20,.6)' }}>
                        {d.a}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Firma />
        </footer>
      </div>
    </Pantalla>
  );
}
